import { Box, CircularProgress } from '@mui/material';
import { useCallback, useEffect, useState } from 'react';
import { useIntl } from 'react-intl';
import { Outlet, useParams, useSearchParams } from 'react-router-dom';
import { z } from 'zod';

import { useAppDispatch } from '../../hooks/global/typedReduxHooks';
import NotFoundPage from '../../Page/public/notFoundPage';
import requestInformationService from '../../services/requestInformationService';
import {
  createRFIConversation,
  setActiveRFIConversation,
} from '../../slices/rfiMessageSlice';
import { setItem, setUniqueId } from '../../slices/securePortalSlice';

import { AuthService } from '~/services/api/auth/endpoints';
import { isMutationSuccess } from '~/services/api/rest-api';

const RequireSecurePortal = () => {
  const intl = useIntl();

  const [init, setInit] = useState<boolean>(false);
  const [loading, setLoading] = useState<boolean>(true);
  const [isValid, setIsValid] = useState<boolean>(false);
  const dispatch = useAppDispatch();
  const { uniqueId } = useParams();
  const [searchParams] = useSearchParams();
  const region = z.string().catch('').parse(searchParams.get('region'));
  const code = z.string().catch('').parse(searchParams.get('code'));
  const [loginRfiRecipient, mutation] = AuthService.endpoints.loginRfiRecipient.useMutation();

  const checkUniqueId = useCallback(async () => {
    if (region !== 'ap' && region !== 'eu') {
      return;
    }

    if (code) {
      // If the 'code' query parameter exists, remove it from the URL
      const login = await loginRfiRecipient({ code });
      if (isMutationSuccess(login)) {
        localStorage.setItem('access_token', login.data.access_token);
        localStorage.setItem('refresh_token', login.data.refresh_token);
        
        const newUniqueId = typeof uniqueId == 'undefined' ? '' : uniqueId;
        requestInformationService
          .getRecipientData(newUniqueId)
          .then((response) => {
            dispatch(setItem(response.data));
            dispatch(setUniqueId(typeof uniqueId == 'undefined' ? '' : uniqueId));
            if (response.data.rfi_conversation) {
              if (response.data.status === 1 || response.data.status === 2) {
                dispatch(
                  setActiveRFIConversation(response.data.rfi_conversation.id),
                );
              }
              dispatch(
                createRFIConversation(
                  { 
                    ...response.data.rfi_conversation,
                    messages: [], // messages are fetched in RFIMessages component
                    status: response.data.status,
                  },
                ),
              );
            }
            setIsValid(true);
            setLoading(false);
    
            if (response.data.status === 1) {
            //update status to viewed
              requestInformationService.updateStatus(
                2,
                newUniqueId,
                response.data.details,
              );
            }

            if (response.data.status === 2) {
              requestInformationService.rfiConversationIsReadRecipient(response.data.rfi_conversation.id);
            }
          })
          .catch(() => {
            //not valid
            setLoading(false);
          });
    }
  }
}, [code, dispatch, loginRfiRecipient, region, uniqueId]);

  useEffect(() => {
    //fetch recipient data
    if (!init) setInit(true);

    if (init) {
      checkUniqueId();
    }
  }, [init, checkUniqueId]);

  if ((region !== 'eu' && region !== 'ap') || mutation.error) {
    return ( 
      <NotFoundPage
        statusCode='404'
        statusMessage={intl.formatMessage({
          defaultMessage: 'Page not found.',
        })}
      />
    );
  }

  return (
    <>
      {loading && (
        <Box
          sx={{
            width: '100%',
            height: '100vh',
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
          }}
        >
          <CircularProgress
            size={60}
            style={{ marginLeft: '19px', color: 'black' }}
          />
        </Box>
      )}
      {isValid && !loading && init && <Outlet />}
      {!isValid && !loading && init && (
        <NotFoundPage
          statusCode='404'
          statusMessage={intl.formatMessage({
            defaultMessage: 'Page not found.',
          })}
        />
      )}
    </>
  );
};

export default RequireSecurePortal;
